import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  YearData, 
  calculateTaperedAllowance, 
  calculateCarryForward 
} from '../pensionCalculator.ts';

// Helper to generate tax year string from year number
const generateTaxYear = (year: number): string => {
  return `${year}/${(year + 1).toString().slice(-2)}`;
};

// Helper to create new year with default values
const createNewYear = (year: number): YearData => ({
  id: year,
  taxYear: generateTaxYear(year),
  thresholdIncome: 0,
  adjustedIncome: 0,
  taperedAllowance: calculateTaperedAllowance(0, 0, year),
  contribution: 0,
  shortfall: 0,
  carryForwardAvailable: 0,
  carryForwardUsed: 0,
  carryForwardRemaining: 0,
  carryForwardBreakdown: [],
  carryForwardUsedBreakdown: [],
  carryForwardUsedByFuture: [],
  canUseThisYear: 0,
  missingYearWarning: false,
  originalCarryForward: 0,
  carryForwardAvailableFromPrevious: 0
});

// State interface
interface PensionState {
  years: YearData[];
  
  // Actions
  updateYear: (yearId: number, updates: Partial<YearData>) => void;
  addYear: (year: number) => void;
  removeYear: (yearId: number) => void;
  clearAllData: () => void;
  recalculateAll: () => void;
  
  // Import/Export
  exportData: () => string;
  importData: (data: string) => boolean;
  
  // Utility
  getYearById: (yearId: number) => YearData | undefined;
  getSortedYears: () => YearData[];
}

// Default years based on current date (similar to original implementation)
const getCurrentYear = () => new Date().getFullYear();
const createDefaultYears = (): YearData[] => {
  const currentYear = getCurrentYear();
  const years = [];
  
  // Create 4 years: current-3, current-2, current-1, current with sample data
  for (let i = 3; i >= 0; i--) {
    const year = createNewYear(currentYear - i);
    
    // Add sample data for demonstration
    if (i === 3) { // Oldest year
      year.thresholdIncome = 150000;
      year.adjustedIncome = 200000;
      year.contribution = 30000;
    } else if (i === 2) {
      year.thresholdIncome = 160000;
      year.adjustedIncome = 210000;
      year.contribution = 35000;
    } else if (i === 1) {
      year.thresholdIncome = 170000;
      year.adjustedIncome = 220000;
      year.contribution = 40000;
    } else { // Current year
      year.thresholdIncome = 180000;
      year.adjustedIncome = 230000;
      year.contribution = 45000;
    }
    
    // Recalculate tapered allowance with sample data
    year.taperedAllowance = calculateTaperedAllowance(
      year.thresholdIncome,
      year.adjustedIncome,
      year.id
    );
    
    years.push(year);
  }
  
  // Calculate carry forward for the sample data
  return calculateCarryForward(years);
};

// Create the store
export const usePensionStore = create<PensionState>()(
  persist(
    (set, get) => ({
      years: createDefaultYears(),
      
      updateYear: (yearId: number, updates: Partial<YearData>) => {
        set((state) => {
          const newYears = state.years.map(year => {
            if (year.id === yearId) {
              const updatedYear = { ...year, ...updates };
              
              // Recalculate tapered allowance if income values changed
              if (updates.thresholdIncome !== undefined || updates.adjustedIncome !== undefined) {
                updatedYear.taperedAllowance = calculateTaperedAllowance(
                  updatedYear.thresholdIncome,
                  updatedYear.adjustedIncome,
                  updatedYear.id
                );
              }
              
              return updatedYear;
            }
            return year;
          });
          
          // Recalculate all carry forward logic after any update
          const calculatedYears = calculateCarryForward(newYears.sort((a, b) => a.id - b.id));
          
          return { years: calculatedYears };
        });
      },
      
      addYear: (year: number) => {
        set((state) => {
          // Check if year already exists
          if (state.years.some(y => y.id === year)) {
            return state;
          }
          
          const newYear = createNewYear(year);
          const updatedYears = [...state.years, newYear].sort((a, b) => a.id - b.id);
          const calculatedYears = calculateCarryForward(updatedYears);
          
          return { years: calculatedYears };
        });
      },
      
      removeYear: (yearId: number) => {
        set((state) => {
          const filteredYears = state.years.filter(year => year.id !== yearId);
          const calculatedYears = calculateCarryForward(filteredYears);
          
          return { years: calculatedYears };
        });
      },
      
      clearAllData: () => {
        set(() => ({
          years: createDefaultYears()
        }));
      },
      
      recalculateAll: () => {
        set((state) => {
          const recalculatedYears = state.years.map(year => ({
            ...year,
            taperedAllowance: calculateTaperedAllowance(
              year.thresholdIncome,
              year.adjustedIncome,
              year.id
            )
          }));
          
          const calculatedYears = calculateCarryForward(recalculatedYears);
          
          return { years: calculatedYears };
        });
      },
      
      exportData: () => {
        const state = get();
        const exportObject = {
          version: "1.0",
          exportDate: new Date().toISOString(),
          data: state.years
        };
        
        return btoa(JSON.stringify(exportObject));
      },
      
      importData: (data: string) => {
        try {
          const decoded = atob(data);
          const parsed = JSON.parse(decoded);
          
          // Validate the import data structure
          if (!parsed.version || !parsed.data || !Array.isArray(parsed.data)) {
            return false;
          }
          
          // Validate each year object has required fields
          const isValid = parsed.data.every((year: any) => 
            typeof year.id === 'number' &&
            typeof year.taxYear === 'string' &&
            typeof year.thresholdIncome === 'number' &&
            typeof year.adjustedIncome === 'number' &&
            typeof year.contribution === 'number'
          );
          
          if (!isValid) {
            return false;
          }
          
          // Import the data and recalculate
          set(() => {
            const importedYears = parsed.data.map((year: any) => ({
              ...createNewYear(year.id),
              ...year,
              taperedAllowance: calculateTaperedAllowance(
                year.thresholdIncome,
                year.adjustedIncome,
                year.id
              )
            }));
            
            const calculatedYears = calculateCarryForward(importedYears.sort((a, b) => a.id - b.id));
            
            return { years: calculatedYears };
          });
          
          return true;
        } catch (error) {
          console.error('Import failed:', error);
          return false;
        }
      },
      
      getYearById: (yearId: number) => {
        return get().years.find(year => year.id === yearId);
      },
      
      getSortedYears: () => {
        return get().years.sort((a, b) => a.id - b.id);
      }
    }),
    {
      name: 'uk-pension-calculator-storage',
      // Only persist the years data, not the functions
      partialize: (state) => ({ years: state.years }),
    }
  )
);